import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, List, ListItem, ListItemText, TextField, Button, Card, CardContent, CardMedia, Input, ListItemIcon, FormControl, Select, MenuItem, InputAdornment, InputLabel, IconButton, Tooltip, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LocationSelector from '../components/LocationSelector';
import { useCart } from '../contexts/CartContext';
import { 
  Smartphone,
  LaptopMac,
  Tv,
  Kitchen,
  AcUnit,
  CameraAlt,
  Headphones,
  Watch,
  Lightbulb,
  DevicesOther,
  Search,
  Store,
  LocationOn,
  Close,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';

// Add keyframes for cursor blinking
const keyframes = `
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

// Inject the keyframes into the document
const injectGlobalStyles = () => {
  const styleEl = document.createElement('style');
  styleEl.innerHTML = keyframes;
  document.head.appendChild(styleEl);
  return () => document.head.removeChild(styleEl);
};

// Styled components
const Sidebar = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  maxWidth: '260px',
  width: '100%',
}));

const CategoryList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const StoreList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
  color: 'white',
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
}));

const MainContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const ElectronicCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s',
  width: '100%',
  margin: '0 auto',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[6],
  },
}));

const StyledBadge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: '4px 8px',
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 'bold',
}));

const SearchBarHeader = styled(Paper)(({ theme }) => ({
  position: 'relative',
  minHeight: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'nowrap',
  gap: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  overflow: 'hidden',
  zIndex: 1,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
}));

const VideoBackground = styled('video')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: -1,
});

const SearchOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(45deg, rgba(94, 53, 177, 0.82) 30%, rgba(30, 136, 229, 0.72) 90%)',
  zIndex: -1,
}));

// Default logo component for electronic stores
const DefaultStoreLogo = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  backgroundColor: '#e3f2fd',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #bbdefb',
}));

// Electronics categories
const electronics = [
  {
    category: { bangla: "মোবাইল ও ট্যাব", english: "Mobiles & Tablets" },
    icon: <Smartphone />,
    items: [
      { bangla: "স্মার্টফোন", english: "Smartphones", icon: <Smartphone /> },
      { bangla: "বাটন ফোন", english: "Feature Phones", icon: <Smartphone /> },
      { bangla: "ট্যাবলেট", english: "Tablets", icon: <Smartphone /> },
      { bangla: "মোবাইল এক্সেসরিজ", english: "Mobile Accessories", icon: <DevicesOther /> }
    ]
  },
  {
    category: { bangla: "কম্পিউটার ও ল্যাপটপ", english: "Computers & Laptops" },
    icon: <LaptopMac />,
    items: [
      { bangla: "ল্যাপটপ", english: "Laptops", icon: <LaptopMac /> },
      { bangla: "ডেস্কটপ", english: "Desktops", icon: <LaptopMac /> },
      { bangla: "মনিটর", english: "Monitors", icon: <LaptopMac /> },
      { bangla: "প্রিন্টার", english: "Printers", icon: <LaptopMac /> },
      { bangla: "কম্পিউটার এক্সেসরিজ", english: "Accessories", icon: <DevicesOther /> }
    ]
  },
  {
    category: { bangla: "টেলিভিশন", english: "Television" },
    icon: <Tv />,
    items: [
      { bangla: "স্মার্ট টিভি", english: "Smart TVs", icon: <Tv /> },
      { bangla: "LED টিভি", english: "LED TVs", icon: <Tv /> },
      { bangla: "TV এক্সেসরিজ", english: "TV Accessories", icon: <DevicesOther /> }
    ]
  },
  {
    category: { bangla: "হোম অ্যাপ্লায়েন্স", english: "Home Appliances" },
    icon: <Kitchen />,
    items: [
      { bangla: "ফ্রিজ", english: "Refrigerators", icon: <Kitchen /> },
      { bangla: "ওয়াশিং মেশিন", english: "Washing Machines", icon: <Kitchen /> },
      { bangla: "মাইক্রোওয়েভ ওভেন", english: "Microwave Ovens", icon: <Kitchen /> },
      { bangla: "ব্লেন্ডার ও জুসার", english: "Blenders & Juicers", icon: <Kitchen /> },
      { bangla: "রাইস কুকার", english: "Rice Cookers", icon: <Kitchen /> },
      { bangla: "ইলেকট্রিক কেটলি", english: "Electric Kettles", icon: <Kitchen /> }
    ]
  },
  {
    category: { bangla: "এয়ার কন্ডিশনার ও কুলার", english: "Air Conditioners & Coolers" },
    icon: <AcUnit />,
    items: [
      { bangla: "এসি", english: "AC (Air Conditioner)", icon: <AcUnit /> },
      { bangla: "এয়ার কুলার", english: "Air Coolers", icon: <AcUnit /> },
      { bangla: "ফ্যান", english: "Fans", icon: <AcUnit /> }
    ]
  },
  {
    category: { bangla: "ক্যামেরা ও ডিভাইস", english: "Cameras & Devices" },
    icon: <CameraAlt />,
    items: [
      { bangla: "ডিএসএলআর", english: "DSLR Cameras", icon: <CameraAlt /> },
      { bangla: "অ্যাকশন ক্যামেরা", english: "Action Cameras", icon: <CameraAlt /> },
      { bangla: "সিসিটিভি ক্যামেরা", english: "CCTV Cameras", icon: <CameraAlt /> },
      { bangla: "ক্যামেরা এক্সেসরিজ", english: "Camera Accessories", icon: <DevicesOther /> }
    ]
  },
  {
    category: { bangla: "অডিও ও হেডফোন", english: "Audio & Headphones" },
    icon: <Headphones />,
    items: [
      { bangla: "হেডফোন", english: "Headphones", icon: <Headphones /> },
      { bangla: "স্পিকার", english: "Speakers", icon: <Headphones /> },
      { bangla: "হোম থিয়েটার", english: "Home Theater", icon: <Headphones /> },
      { bangla: "মাইক্রোফোন", english: "Microphones", icon: <Headphones /> }
    ]
  },
  {
    category: { bangla: "স্মার্ট ওয়াচ ও গ্যাজেট", english: "Smart Watches & Gadgets" },
    icon: <Watch />,
    items: [
      { bangla: "স্মার্টওয়াচ", english: "Smart Watches", icon: <Watch /> },
      { bangla: "ফিটনেস ব্যান্ড", english: "Fitness Bands", icon: <Watch /> },
      { bangla: "গ্যাজেট এক্সেসরিজ", english: "Gadget Accessories", icon: <DevicesOther /> }
    ]
  },
  {
    category: { bangla: "লাইট ও ইলেকট্রিক", english: "Lighting & Electrical" },
    icon: <Lightbulb />,
    items: [
      { bangla: "ইলেকট্রিক লাইট", english: "Electric Lights", icon: <Lightbulb /> },
      { bangla: "ইমার্জেন্সি লাইট", english: "Emergency Lights", icon: <Lightbulb /> },
      { bangla: "ইউনিভার্সাল প্লাগ", english: "Universal Plugs", icon: <Lightbulb /> },
      { bangla: "মাল্টিপ্লাগ", english: "Multiplugs", icon: <Lightbulb /> }
    ]
  }
];

// List of stores
const stores = [
  {
    name: 'Electronics Hub',
    logo: '/images/logos/ehub.png',
    description: 'আধুনিক ইলেকট্রনিক্সের সেরা ঠিকানা'
  },
  {
    name: 'Gadget World',
    logo: '/images/logos/gadgetworld.png',
    description: 'প্রিমিয়াম গ্যাজেটের শোরুম'
  },
  {
    name: 'Digital Plaza',
    logo: '/images/logos/digitalplaza.png',
    description: 'সকল ধরনের ডিজিটাল ডিভাইস'
  },
  {
    name: 'Tech Valley',
    logo: '/images/logos/techvalley.png',
    description: 'টেকনোলজি পণ্যের বিশ্বস্ত ঠিকানা'
  },
  {
    name: 'Smart Electronics',
    logo: '/images/logos/smartelec.png',
    description: 'স্মার্ট ডিভাইসের সমাহার'
  },
  {
    name: 'Device Center',
    logo: '/images/logos/devicecenter.png',
    description: 'ইলেকট্রনিক ডিভাইসের কেন্দ্র'
  }
];

// Sample electronics data
const electronicsData = [
  {
    id: 1,
    name: 'স্যামসাং গ্যালাক্সি এস২৩',
    englishName: 'Samsung Galaxy S23',
    category: 'স্মার্টফোন',
    store: 'Electronics Hub',
    price: '৳১২৯,৯৯০.০০',
    image: '/images/electronics/e1.jpg',
    description: 'ফ্ল্যাগশিপ স্মার্টফোন',
    brand: 'Samsung'
  },
  {
    id: 2,
    name: 'ম্যাকবুক প্রো',
    englishName: 'MacBook Pro',
    category: 'ল্যাপটপ',
    store: 'Gadget World',
    price: '৳১৮৯,৯৯০.০০',
    image: '/images/electronics/e2.jpg',
    description: 'প্রফেশনাল ল্যাপটপ',
    brand: 'Apple'
  },
  {
    id: 3,
    name: 'সনি ব্রাভিয়া ৫৫"',
    englishName: 'Sony Bravia 55"',
    category: 'স্মার্ট টিভি',
    store: 'Digital Plaza',
    price: '৳৯৯,৯৯০.০০',
    image: '/images/electronics/e3.jpg',
    description: '4K স্মার্ট টিভি',
    brand: 'Sony'
  },
  {
    id: 4,
    name: 'ওয়ালটন ফ্রিজ',
    englishName: 'Walton Refrigerator',
    category: 'ফ্রিজ',
    store: 'Tech Valley',
    price: '৳৪৯,৯৯০.০০',
    image: '/images/electronics/e4.jpg',
    description: 'ইনভার্টার টেকনোলজি ফ্রিজ',
    brand: 'Walton'
  },
  {
    id: 5,
    name: 'ক্যানন ডিএসএলআর',
    englishName: 'Canon DSLR',
    category: 'ডিএসএলআর',
    store: 'Smart Electronics',
    price: '৭৯,৯৯০.০০',
    image: '/images/electronics/e5.jpg',
    description: 'প্রফেশনাল ক্যামেরা',
    brand: 'Canon'
  },
  {
    id: 6,
    name: 'এপল ওয়াচ সিরিজ ৮',
    englishName: 'Apple Watch Series 8',
    category: 'স্মার্টওয়াচ',
    store: 'Device Center',
    price: '৫৯,৯৯০.০০',
    image: '/images/electronics/e6.jpg',
    description: 'প্রিমিয়াম স্মার্টওয়াচ',
    brand: 'Apple'
  }
];

const bazars = [
  { bn: 'সকল বাজার', en: 'All Bazars' },
  { bn: 'চাচকৈড় বাজার', en: 'Chachkoir Bazar' },
  { bn: 'খুবজিপুর বাজার', en: 'Khubipur Bazar' },
  { bn: 'নাজিরপুর বাজার', en: 'Nazirpur Bazar' },
  { bn: 'বিলদোহর বাজার', en: 'Bildohar Bazar' },
  { bn: 'চাটখিল বাজার', en: 'Chatkhil Bazar' },
  { bn: 'সোনাইমুড়ি বাজার', en: 'Sonaimuri Bazar' },
  { bn: 'কেন্দ্রীয় বাজার', en: 'Central Bazar' },
  { bn: 'নিউ মার্কেট', en: 'New Market' },
  { bn: 'গুলিস্তান বাজার', en: 'Gulistan Bazar' },
  { bn: 'মৌচাক বাজার', en: 'Mouchak Bazar' },
  { bn: 'রকেট বাজার', en: 'Rocket Bazar' },
  { bn: 'কাওরান বাজার', en: 'Kawran Bazar' },
  { bn: 'ফার্মগেট বাজার', en: 'Farmgate Bazar' },
  { bn: 'মিরপুর বাজার', en: 'Mirpur Bazar' },
  { bn: 'উত্তরা বাজার', en: 'Uttara Bazar' },
  { bn: 'ধানমন্ডি বাজার', en: 'Dhanmondi Bazar' },
  { bn: 'মোহাম্মদপুর বাজার', en: 'Mohammadpur Bazar' },
  { bn: 'বনানী বাজার', en: 'Banani Bazar' },
  { bn: 'গুলশান বাজার', en: 'Gulshan Bazar' }
];

const ElectronicsBazar = () => {
  const { addToCart } = useCart();
  const [searchQuery, setSearchQuery] = useState('');
  const [typingText, setTypingText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingIndex, setTypingIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('সকল জেলা');
  const [selectedUpazilla, setSelectedUpazilla] = useState('সকল উপজেলা');
  const [selectedBazar, setSelectedBazar] = useState('সকল বাজার');
  const [districtSearchQuery, setDistrictSearchQuery] = useState('');
  const [upazillaSearchQuery, setUpazillaSearchQuery] = useState('');
  const [bazarSearchQuery, setBazarSearchQuery] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(150);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Typing animation effect
  const placeholderTexts = [
    'ইলেকট্রনিক্সের নাম লিখুন...',
    'ব্র্যান্ডের নাম লিখুন...',
    'দোকানের নাম লিখুন...',
    'ক্যাটেগরির নাম লিখুন...',
    'ডিভাইসের ধরন লিখুন...'
  ];

  // Inject keyframes for the blinking cursor animation
  useEffect(() => {
    const cleanup = injectGlobalStyles();
    return cleanup;
  }, []);

  // Typewriter effect for search placeholders
  useEffect(() => {
    const currentPlaceholder = placeholderTexts[placeholderIndex];
    
    const handleTyping = () => {
      if (isDeleting) {
        setTypingText(prev => prev.slice(0, -1));
        setTypingSpeed(50);
        
        if (typingText === '') {
          setIsDeleting(false);
          setPlaceholderIndex((prev) => (prev + 1) % placeholderTexts.length);
          setTypingSpeed(150);
        }
      } else {
        setTypingText(currentPlaceholder.slice(0, typingText.length + 1));
        setTypingSpeed(150);
        
        if (typingText === currentPlaceholder) {
          setTypingSpeed(2000);
          setIsDeleting(true);
        }
      }
    };
    
    const timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [typingText, isDeleting, placeholderIndex, placeholderTexts, typingSpeed]);

  const handleCategoryClick = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const handleSubCategoryClick = (mainCategoryKey, subCategory) => {
    setSelectedCategory(mainCategoryKey);
    setSelectedSubCategory(subCategory);
  };

  const handleStoreClick = (store) => {
    setSelectedStore(store.name);
  };

  const handleDistrictChange = (event) => {
    setSelectedDistrict(event.target.value);
  };

  const handleUpazillaChange = (event) => {
    setSelectedUpazilla(event.target.value);
  };

  const handleBazarChange = (event) => {
    setSelectedBazar(event.target.value);
  };

  const handleAddToCart = (product) => {
    console.log('ElectronicsBazar - Adding product to cart:', product);
    
    const cartProduct = {
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      store: product.store,
      stock: product.stock || 10
    };
    
    addToCart(cartProduct, 1);
  };

  const filteredElectronics = electronicsData.filter((electronic) => {
    const matchesCategory = !selectedCategory || 
      (selectedSubCategory ? electronic.category === selectedSubCategory : 
       electronics.find(cat => cat.category.bangla === selectedCategory)?.items.some(item => item.bangla === electronic.category));
    const matchesStore = !selectedStore || electronic.store === selectedStore;
    const matchesSearch = !searchQuery || 
      electronic.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      electronic.englishName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesStore && matchesSearch;
  });

  // Filter bazars based on search query
  const filteredBazars = bazars.filter(
    bazar => bazar.bn.toLowerCase().includes(bazarSearchQuery.toLowerCase())
  );

  return (
    <>
      <Header />
      <Box sx={{ flexGrow: 1, p: 2, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Search Header Bar */}
        <SearchBarHeader>
          <VideoBackground autoPlay muted loop>
            <source src="/videos/electronics.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </VideoBackground>
          <SearchOverlay />          {/* Location Selector */}
          <LocationSelector 
            variant="search-bar"
            onLocationChange={(location) => {
              // Optional: Handle location change if needed
              console.log('Location changed:', location);
            }}
          />

          {/* Animated Search Field */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder={typingText}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ 
              mx: 2, 
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              borderRadius: 2,
              boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              },
              zIndex: 2,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ 
                    opacity: isDeleting ? 0 : 1, 
                    transition: 'opacity 0.3s',
                    height: '1.2em', 
                    width: '0.15em', 
                    backgroundColor: 'primary.main',
                    display: 'inline-block',
                    animation: 'blink 1s step-end infinite'
                  }}/>
                </InputAdornment>
              )
            }}
          />
        </SearchBarHeader>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Sidebar */}
          <Box sx={{ 
            width: { xs: '100%', md: '260px' }, 
            minWidth: { md: '260px' },
            pr: { md: 2 }, 
            mb: { xs: 2, md: 0 } 
          }}>
            <Sidebar elevation={3}>
              <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionHeader>
                  <Smartphone sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    ইলেকট্রনিক্সের ধরন
                  </Typography>
                </SectionHeader>
                <CategoryList sx={{ pl: 1, pr: 1 }}>
                  {electronics.map((categoryData) => {
                    const categoryKey = categoryData.category.bangla;
                    return (
                      <React.Fragment key={categoryKey}>
                        <ListItem
                          button
                          onClick={() => handleCategoryClick(categoryKey)}
                          selected={selectedCategory === categoryKey}
                          sx={{ py: 1 }}
                        >
                          <ListItemIcon>
                            {categoryData.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={categoryData.category.bangla}
                            secondary={categoryData.category.english}
                            primaryTypographyProps={{ 
                              style: { 
                                fontWeight: selectedCategory === categoryKey ? 'bold' : 'normal',
                                fontSize: '0.9rem'
                              } 
                            }}
                            secondaryTypographyProps={{ 
                              style: { 
                                fontSize: '0.75rem',
                                color: 'text.secondary'
                              } 
                            }}
                          />
                          {expandedCategories[categoryKey] ? <ExpandLess /> : <ExpandMore />}
                        </ListItem>
                        <Collapse in={expandedCategories[categoryKey]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {categoryData.items.map((item) => (
                              <ListItem
                                button
                                key={`${categoryKey}-${item.bangla}`}
                                sx={{ pl: 4, py: 0.5 }}
                                selected={selectedCategory === categoryKey && selectedSubCategory === item.bangla}
                                onClick={() => handleSubCategoryClick(categoryKey, item.bangla)}
                              >
                                <ListItemIcon>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText 
                                  primary={item.bangla}
                                  secondary={item.english}
                                  primaryTypographyProps={{ 
                                    style: { 
                                      fontSize: '0.8rem',
                                      fontWeight: selectedCategory === categoryKey && selectedSubCategory === item.bangla ? 'bold' : 'normal'
                                    } 
                                  }}
                                  secondaryTypographyProps={{ 
                                    style: { fontSize: '0.7rem' } 
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  })}
                </CategoryList>

                <SectionHeader sx={{ mt: 2 }}>
                  <Store sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    দোকানের তালিকা
                  </Typography>
                </SectionHeader>
                <StoreList sx={{ pl: 1, pr: 1 }}>
                  {stores.map((store) => (
                    <ListItem
                      button
                      key={store.name}
                      selected={selectedStore === store.name}
                      onClick={() => handleStoreClick(store)}
                      sx={{ py: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 35 }}>
                        <DefaultStoreLogo>
                          <Store sx={{ fontSize: 12, color: '#1976d2' }} />
                        </DefaultStoreLogo>
                      </ListItemIcon>
                      <ListItemText 
                        primary={store.name}
                        secondary={store.description}
                        primaryTypographyProps={{ 
                          fontSize: '0.875rem',
                          fontWeight: selectedStore === store.name ? 'bold' : 'normal' 
                        }}
                        secondaryTypographyProps={{ 
                          fontSize: '0.75rem',
                          color: 'text.secondary'
                        }}
                      />
                    </ListItem>
                  ))}
                </StoreList>
              </Box>
            </Sidebar>
          </Box>

          {/* Main Content */}
          <Box sx={{ 
            width: { xs: '100%', md: 'calc(100% - 260px)' },
            flexGrow: 1 
          }}>
            <MainContent>
              {/* Electronics Grid */}
              {filteredElectronics.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)', // Mobile portrait: 1 card per row
                      sm: 'repeat(2, 1fr)', // Mobile horizontal: 2 cards per row
                      md: 'repeat(5, 1fr)', // Laptop: 5 cards per row
                      lg: 'repeat(6, 1fr)', // Big screen: 6 cards per row
                      '@media (min-width: 1980px)': {
                        gridTemplateColumns: 'repeat(9, 1fr)' // Extra large screens: 9 cards per row
                      }
                    },
                    width: '100%',
                  }}
                >
                  {filteredElectronics.map((product) => (
                    <Box key={product.id} sx={{ display: 'flex', width: '100%' }}>
                      <ElectronicCard sx={{ width: '100%' }}>
                        <Box sx={{ position: 'relative' }}>
                          <CardMedia
                            component="img"
                            height="160"
                            image={product.image}
                            alt={product.name}
                            sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5', pt: 1 }}
                            onError={(e) => {
                              e.target.src = '/images/placeholder.png';
                            }}
                          />
                          <StyledBadge>{product.category}</StyledBadge>
                        </Box>
                        <CardContent sx={{
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          pb: '16px !important' // Override default padding bottom
                        }}>
                          <Box>
                            <Typography gutterBottom variant="h6" component="div">
                              {product.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {product.englishName}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.primary">
                                <strong>ব্র্যান্ড:</strong> {product.brand}
                              </Typography>
                              <Typography variant="body2" color="text.primary">
                                <strong>দোকান:</strong> {product.store}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{
                            mt: 'auto', // Push this box to the bottom
                            pt: 2 // Add some spacing from the content above
                          }}>
                            <Typography variant="h6" color="primary" sx={{ mb: 1, textAlign: 'center' }}>
                              {product.price}
                            </Typography>
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 1
                            }}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                fullWidth
                              >
                                কিনুন
                              </Button>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                fullWidth
                                onClick={() => handleAddToCart(product)}
                              >
                                ব্যাগে নিন
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </ElectronicCard>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                  <Smartphone sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    কোন ইলেকট্রনিক্স পাওয়া যায়নি
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    অনুগ্রহ করে আপনার ফিল্টার বা সার্চ পরিবর্তন করুন
                  </Typography>
                  <Button 
                    variant="outlined" 
                    sx={{ mt: 2 }}
                    onClick={() => {
                      setSelectedCategory('');
                      setSelectedSubCategory('');
                      setSelectedStore('');
                      setSearchQuery('');
                    }}
                  >
                    সব ফিল্টার মুছুন
                  </Button>
                </Paper>
              )}
            </MainContent>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: "50px" }}>
        <Footer />
      </Box>
    </>
  );
};

export default ElectronicsBazar;
